import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { INITIAL_ROW_SELECTION_MODEL } from '../../components/DataGridV2/constants';
import { makeStyles } from '@mui/styles';
import DataGridV2 from '../../components/DataGridV2';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { Add, Build, Delete, FileCopyOutlined, Mail } from '@mui/icons-material';
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../_constants';
import { useSelector } from 'react-redux';
import { authHeader, formatDate, getLocalStorageItem, history, setLocalStorageItem } from '../../_helpers';
import SelectOrganizationModal from './SelectOrganizationModal';
import SendReminderDialog from './SendReminderDialog';
import { getIdsForGridSelectedRows } from '../../_helpers/getIdsForGridSelectedRows';
import { getRepairRequestWorkOrderColumns } from './repairRequestWorkOrderUtils';

const RepairRequestWorkOrdersGrid = ({
  repairRequest,
  hasErrors,
  building,
  GetRepairRequestDetails,
}) => {
  const { webApiUrl } = window.appConfig;
  const classes = useStyles();
  const theme = useTheme();
  const app = useSelector((state) => state.app);
  const { selected, } = useSelector(state => state.buildings)
  const [highlightErrors, setHighlightErrors] = useState(false);
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" : app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";;
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [addWorkOrderModalOpen, setAddWorkOrderModalOpen] = useState()
  const [copyWorkOrderModalOpen, setCopyWorkOrderModalOpen] = useState()
  const [workorderPopoverOpenLoader, setWorkorderPopoverOpenLoader] = useState(false)
  const [sendReminderAnchorEl, setSendReminderAnchorEl] = useState(null);

  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_PAGINATION[keyPrefix], undefined
  ))
  const rows = repairRequest?.resolvers?.map((x) => ({ ...x, id: x.resolverId }))

  const copyWorkOrder = async (organisationId) => {
    const selectedIds = Array.from(rowSelectionModel?.ids || []);
    const resolverId = selectedIds[0];

    if (!repairRequest || repairRequest.completed || selectedIds.length !== 1) return;

    const url = `${webApiUrl}api/RepairRequest/CopyWorkOrder/${organisationId}/${resolverId}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: authHeader("application/json"),
      });

      const res = await response.json();

      if (res.success === true && !!res.resolverId) {
        history.push(workOrderRedirectHelper(res.resolverId));
      } else {
        alert(t("general.api.error"));
      }
    } catch (e) {
      console.error("CopyWorkOrder error", e);
      alert(t("general.api.error"));
    }
  };

  const resolverIds = useMemo(() => {
    return getIdsForGridSelectedRows(rowSelectionModel, rows)
  }, [rowSelectionModel, rows]);

  const canSendReminderSelectedWorkOrders = () => {
    if (repairRequest && resolverIds.length > 0) {
      const resolvers = repairRequest.resolvers.filter((x) => resolverIds.includes(x.resolverId));
      for (var i = 0; i < resolvers.length; i++) {
        const resolver = resolvers[i];
        if (resolver.status === 3 || resolver.status === 4 || (resolver.status === 0 && !resolver.workOrderNumber)) {
          return false;
        }
      }
    }
    return true;
  }

 const emails = useMemo(() => {
  return repairRequest.resolvers
    .filter(resolver => resolverIds.includes(resolver.resolverId))
    .map(resolver => resolver?.email)
}, [repairRequest, resolverIds]);

const resolverStatus = useMemo(() => {
  return repairRequest.resolvers
    .filter(resolver => resolverIds.includes(resolver.resolverId))
    .map(resolver => resolver?.status)
}, [repairRequest, resolverIds]);
    
  const AddRepairRequestResolver = async (organisationId) => {
    if (!repairRequest || repairRequest.completed === true) return;

    const url = `${webApiUrl}api/RepairRequest/AddRepairRequestResolver/${encodeURIComponent(
      repairRequest.requestId
    )}`;

    const formData = new FormData();
    formData.append("organisationId", organisationId);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: authHeader(),
        body: formData,
      });

      const res = await response.json();

      if (res.success === true && !!res.resolverId) {
        history.push(workOrderRedirectHelper(res.resolverId));
      } else {
        alert(t("general.api.error"));
      }
    } catch (error) {
      console.error("AddRepairRequestResolver error:", error);
      alert(t("general.api.error"));
    }
  };

  const canDeleteSelectedWorkOrders = () => {
    if (repairRequest && resolverIds.length > 0) {
      const resolvers = repairRequest.resolvers.filter((x) => resolverIds.includes(x.resolverId));
      for (var i = 0; i < resolvers.length; i++) {
        const resolver = resolvers[i];
        if (resolver.status !== 0 || (resolver.status === 0 && !!resolver.workOrderNumber)) {
          return false;
        }
      }
    }
    return true;
  }


  const deleteRepairRequestResolvers = async () => {
    const selectedIds = Array.from(rowSelectionModel?.ids || []);
    if (selectedIds && selectedIds.length > 0 && canDeleteSelectedWorkOrders()) {
      const url = `${webApiUrl}api/RepairRequest/DeleteResolvers/`;

      const requestOptions = {
        method: "DELETE",
        headers: authHeader("application/json"),
        body: JSON.stringify(resolverIds),
      };

      try {
        const response = await fetch(url, requestOptions);
        const result = await response.json();

        if (response.ok && result?.success) {
          GetRepairRequestDetails(true);
        } else {
          console.error("Delete failed", result);
          alert(t("general.api.error"));
        }
      } catch (error) {
        console.error("Delete error:", error);
        alert(t("general.api.error"));
      } finally {
      }
    }
  };

  const workOrderRedirectHelper = (resolverId) => {
    const url = app !== apps.aftercare ? "/werk/" + selected.projectNo + "/werkbon/" + resolverId : `/nazorg/werkbon/${resolverId}`;
    return url;
  }

  const columns = getRepairRequestWorkOrderColumns({ t, keyPrefix, i18n, apiRef, workOrderRedirectHelper, classes, theme })


  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const getToolbarProps = useMemo(() => {
    const canShowCopyWorkOrder = repairRequest && repairRequest.completed !== true && resolverIds && resolverIds.length === 1;

    return {
      toolbarStartActions: [
        {
          title: t("general.add"),
          icon: Add,
          disabled: (hasErrors && highlightErrors) || workorderPopoverOpenLoader || repairRequest?.completed === true,
          loading: workorderPopoverOpenLoader,
          onClick: () => {
            if (hasErrors) {
              setHighlightErrors(true);
            } else {
              setAddWorkOrderModalOpen(true);
            }
          }
        },
      ],
      taskMenuListItems: resolverIds.length > 0
        ? [

          ...(canShowCopyWorkOrder
            ? [
              {
                title: t("workOrders.copyWorkOrder"),
                icon: (props) => <FileCopyOutlined {...props} />,
                onClick: (() => setCopyWorkOrderModalOpen(true)),
              },
            ]
            : []),
          {
            title: t("repairRequest.sendReminder"),
            icon: (props) => <Mail {...props} />,
            onClick: (e) => setSendReminderAnchorEl(e.currentTarget),
            disabled: repairRequest?.completed === true || !canSendReminderSelectedWorkOrders(),
          },
          {
            title: t("general.delete"),
            icon: (props) => <Delete {...props} />,
            onClick: deleteRepairRequestResolvers,
            disabled: repairRequest?.completed === true || !canDeleteSelectedWorkOrders()
          },
        ]
        : [],
      title: t("workOrders.title"),
    };
  }, [repairRequest, rowSelectionModel, t, hasErrors, highlightErrors, workorderPopoverOpenLoader,]);

  const getRepairRequestWorkOrdersForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
    const userSelectedRows = isAllSelected ? rows : idsToExport.map(id => rows?.find(item => item?.resolverId === id));
    const results = userSelectedRows?.map(item => {
      const result = {};
      Object.keys(item).forEach(key => {
        let value = item[key];
        const isDateTimeField = columns.some(x => x.field === key && x.type === 'dateTime');
        const isDateField = columns.some(x => x.field === key && x.type === 'date');

        if (isDateTimeField) {
          value = item[key] ? formatDate(new Date(item[key]), true) : "";
        } else if (isDateField) {
          value = item[key] ? formatDate(new Date(item[key])) : "";
        }
        else if (key === "status") {
          value = value !== undefined ? t(`resolver.status.${value}`) : "";
        }
        result[key] = value;
      });

      return result;
    });

    return results;
  };

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
        <Box>
          <Typography component="h2" variant="h6" mb={1} className="header">
            <Build color="primary" /> &nbsp; {t("general.workOrders")}
          </Typography>
        </Box>
        <Box  sx={{ flex: 1, minHeight: 0 }}>
          <DataGridV2
            localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_SEARCH_TEXT[keyPrefix]}
            localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_COLUMN_FILTER[keyPrefix]}
            localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_COLUMN_SORT[keyPrefix]}
            localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_COLUMN_ORDER[keyPrefix]}
            localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_COLUMN_WIDTH[keyPrefix]}
            localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.REPAIR_REQUEST_WORK_ORDERS_COLUMN_VISIBILITY[keyPrefix]}
            apiRef={apiRef}
            checkboxSelection
            rows={rows}
            columns={columns}
            pagination
            filterMode="client"
            {...(!!paginationModel && { paginationModel })}
            onPaginationModelChange={onPaginationModelChange}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            forceMobileView={true}
            toolbarProps={{
              ...getToolbarProps,
              selectedRows: rowSelectionModel,
              getData: getRepairRequestWorkOrdersForPrintAndDownload,
              title: t('workOrders.title'),

            }}
          />
        </Box>
      </Box>
      {(addWorkOrderModalOpen || copyWorkOrderModalOpen) && (
        <SelectOrganizationModal
          handleCloseOrganizationModal={() => {
            setAddWorkOrderModalOpen(false);
            setCopyWorkOrderModalOpen(false);
          }}
          setLoading={(val) => addWorkOrderModalOpen && setWorkorderPopoverOpenLoader(val)}
          projectId={building && building.projectId}
          onSelect={(item) => addWorkOrderModalOpen ? AddRepairRequestResolver(item.itemId) : copyWorkOrderModalOpen ? copyWorkOrder(item.itemId) : null}
        />
      )}
      {!!sendReminderAnchorEl && (
        <SendReminderDialog
          open={!!sendReminderAnchorEl}
          onClose={() => {
            setSendReminderAnchorEl(null);
          }}
          emails={emails.join(", ")}
          selectedResolverIds={resolverIds}
          canSendReminderSelectedWorkOrders={canSendReminderSelectedWorkOrders()}
          onSendReminder={GetRepairRequestDetails}
          status={resolverStatus}
        />
      )}
    </Container>

  )

}

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,
  },
  lineThrough: {
    textDecoration: 'line-through'
  }
}));
export default RepairRequestWorkOrdersGrid
